import { useEffect, useState } from "react";
import { Download, FileSpreadsheet, Car, Wallet, Wrench, FileText } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { eur, km } from "@/lib/format";

const COLORS = ["#001A3B", "#12386B", "#A5894E", "#4A5568", "#B4552C", "#7A9B8E", "#9AA88F"];

const Card = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
    <div className="mb-4 flex items-center gap-2"><Icon className="h-5 w-5 text-[#001A3B]" /><h2 className="font-heading text-lg font-semibold text-[#18221F]">{title}</h2></div>
    {children}
  </div>
);

export default function Reports() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/reports/summary").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, []);

  const doExport = async (coll, fmt) => {
    try {
      const res = await api.get(`/export/${coll}?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${coll}.${fmt}`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!d) return <div><PageHeader title="Rapports" subtitle="Analyses flotte, financières, maintenance et contrats." /><div className="grid grid-cols-1 gap-6 lg:grid-cols-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}</div></div>;

  return (
    <div>
      <PageHeader title="Rapports" subtitle="Analyses flotte, financières, maintenance et contrats.">
        {["vehicles", "expenses", "maintenance", "contracts"].map((c) => (
          <DropdownMenu key={c}>
            <DropdownMenuTrigger asChild><Button variant="outline" size="sm" data-testid={`report-export-${c}`}><Download className="mr-1.5 h-3.5 w-3.5" />{c}</Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => doExport(c, "xlsx")}><FileSpreadsheet className="mr-2 h-4 w-4" />Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={() => doExport(c, "csv")}><FileSpreadsheet className="mr-2 h-4 w-4" />CSV</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ))}
      </PageHeader>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[["Véhicules", d.fleet.total, Car], ["Coût total", eur(d.finance.total_cost), Wallet], ["Interventions", d.maintenance.count, Wrench], ["Contrats actifs", d.contracts.active, FileText]].map(([l, v, I]) => (
          <div key={l} className="rounded-2xl border border-[#E2E8F0] bg-white p-5"><I className="h-5 w-5 text-[#001A3B]" /><div className="mt-3 font-heading text-2xl font-bold text-[#18221F]">{v}</div><div className="text-sm text-[#4A5568]">{l}</div></div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card icon={Car} title="Répartition par énergie">
          <div className="h-64"><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={d.fleet.by_energy} dataKey="count" nameKey="label" outerRadius={90} label>{d.fleet.by_energy.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer></div>
        </Card>
        <Card icon={Car} title="Répartition par marque">
          <div className="h-64"><ResponsiveContainer width="100%" height="100%"><BarChart data={d.fleet.by_brand} layout="vertical" margin={{ left: 20 }}><CartesianGrid strokeDasharray="3 3" stroke="#EDF1F6" horizontal={false} /><XAxis type="number" tick={{ fontSize: 12, fill: "#8695AB" }} axisLine={false} tickLine={false} /><YAxis type="category" dataKey="label" tick={{ fontSize: 12, fill: "#4A5568" }} width={90} axisLine={false} tickLine={false} /><Tooltip /><Bar dataKey="count" fill="#12386B" radius={[0, 6, 6, 0]} /></BarChart></ResponsiveContainer></div>
        </Card>
        <Card icon={Wallet} title="Véhicules les plus coûteux">
          <div className="h-64"><ResponsiveContainer width="100%" height="100%"><BarChart data={d.finance.top_costly} margin={{ left: 10 }}><CartesianGrid strokeDasharray="3 3" stroke="#EDF1F6" vertical={false} /><XAxis dataKey="registration" tick={{ fontSize: 10, fill: "#8695AB" }} axisLine={false} tickLine={false} /><YAxis tick={{ fontSize: 12, fill: "#8695AB" }} axisLine={false} tickLine={false} width={50} /><Tooltip formatter={(v) => eur(v)} /><Bar dataKey="cost" fill="#001A3B" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer></div>
        </Card>
        <Card icon={FileText} title="Répartition par société">
          <div className="space-y-3 py-2">
            {d.fleet.by_company.map((c, i) => (
              <div key={c.label}><div className="flex justify-between text-sm"><span className="text-[#4A5568]">{c.label}</span><span className="font-semibold text-[#18221F]">{c.count}</span></div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-[#EEF2F8]"><div className="h-full rounded-full" style={{ width: `${(c.count / d.fleet.total) * 100}%`, background: COLORS[i % COLORS.length] }} /></div></div>
            ))}
            <div className="mt-4 flex justify-between border-t border-[#E2E8F0] pt-3 text-sm"><span className="text-[#4A5568]">Kilométrage total flotte</span><span className="font-semibold text-[#18221F]">{km(d.fleet.total_mileage)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#4A5568]">Coût maintenance</span><span className="font-semibold text-[#18221F]">{eur(d.maintenance.cost)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#4A5568]">Contrats à échéance (90j)</span><span className="font-semibold text-[#18221F]">{d.contracts.expiring_90d}</span></div>
          </div>
        </Card>
      </div>
    </div>
  );
}
